import { useCallback, useMemo } from "react";
import type { Animal } from "../data/animals";
import { masterpieces } from "../data/masterpieces";
import { AnimalCard } from "./AnimalCard";
import { useVoiceControl, VoiceButton } from "../voice/VoiceProvider";
import { scrollPage } from "../lib/scroll";
import { heardAny } from "../voice/match";
import { useLocale, useT } from "../i18n/LocaleProvider";
import { aliasesFor, localizeAnimal } from "../i18n/localize";

interface Props {
  onPick: (painting: Animal) => void;
  onHome: () => void;
  completed: Set<string>;
}

export function PaintingsPage({ onPick, onHome, completed }: Props) {
  const t = useT();
  const { locale } = useLocale();
  // `masterpieces` is module data and never went through the localizer.
  const items = useMemo(
    () => masterpieces.map((m) => ({ m, view: localizeAnimal(m, locale) })),
    [locale],
  );
  const onCommand = useCallback(
    (transcript: string): boolean => {
      // Navigation wins over scrolling, so a "home" grouped with a "down" the
      // child said while browsing isn't swallowed by the scroll command.
      if (heardAny(transcript, [...t.commands.home, ...t.commands.back])) {
        onHome();
        return true;
      }
      if (heardAny(transcript, t.commands.up)) {
        scrollPage(-1);
        return true;
      }
      if (heardAny(transcript, t.commands.down)) {
        scrollPage(1);
        return true;
      }
      for (const { m, view } of items) {
        const words = aliasesFor(view, locale);
        if (m.artist) words.push(m.artist.toLowerCase());
        if (heardAny(transcript, words)) {
          onPick(m);
          return true;
        }
      }
      return false;
    },
    [onPick, onHome, t, items, locale],
  );
  useVoiceControl(onCommand);

  return (
    <div className="home">
      <header className="player-header">
        <button className="home-btn" onClick={onHome} aria-label={t.backToHome}>
          {t.home}
        </button>
        <h1>{t.paintingsTitle}</h1>
      </header>
      <p className="subtitle">{t.paintingsSubtitle}</p>

      <div className="control-bar">
        <VoiceButton />
      </div>

      <div className="card-grid">
        {items.map(({ m, view }) => (
          <AnimalCard
            key={m.id}
            animal={view}
            done={completed.has(m.id)}
            subtitle={m.artist ? `${t.by} ${m.artist}` : undefined}
            onClick={() => onPick(m)}
          />
        ))}
      </div>
    </div>
  );
}
